import React, { useCallback, useRef, useEffect, useMemo, useState, memo } from 'react';
import {
  ReactFlow,
  Background,
  useNodesState,
  useEdgesState,
  Connection,
  EdgeTypes,
  Panel,
  ReactFlowProvider,
  useReactFlow,
  ViewportPortal,
  SelectionMode,
  OnNodesChange,
  EdgeChange,
  Edge,
  NodePositionChange,
  NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CustomEdge } from './edges';
import { EdgeParticlesCanvas } from './edges/EdgeParticlesCanvas';
import { GenerateNode } from './nodes';
import GroupNode from './nodes/GroupNode';
import { SelectionToolbar } from './SelectionToolbar';
import { InverseScaleBox } from './nodes/parts/InverseScaleBox';
import { NodeErrorBoundary } from '../ui/ErrorBoundary';
import { useNodeStore } from '../../stores/nodeStore';
import { useGroupStore } from '../../stores/groupStore';
import { useCanvasStore } from '../../stores/canvasStore';
import { useSelectionStore } from '../../stores/selectionStore';
import { useEdgeActiveStore } from '../../stores/edgeActiveStore';
import { Type, Upload, ImageIcon, Film, Mic2, Scan, Edit2, FileText, Scissors } from 'lucide-react';
import { AppNode, NodeType, isGroupNode } from '../../types';
import { NODE_TYPE_CONFIG } from '../../config/nodeTypeConfig';
import { toast } from 'sonner';
import { rafThrottle, debounce } from '../../utils/throttle';
import { MiniMapToolbar } from './MiniMapToolbar';

// 模块级 resize 状态：任何 resize 路径（GroupNode / TextNode）开始时置 true，结束置 false
// FlowEditor 的 useEffect[nodes] 读取此 ref，resize 期间跳过全量重建 localNodes
// 避免所有节点 wrapper 新建 → ReactFlow diff 所有节点 → backdrop-filter 重合成 → 频闪
const isResizingAnyNode = { current: false };
export const startNodeResize = () => { isResizingAnyNode.current = true; };
export const endNodeResize = () => { isResizingAnyNode.current = false; };

type FlowNode = {
  id: string;
  type: NodeType;
  position: { x: number; y: number };
  data: Record<string, unknown> & { progress?: string };
  selected?: boolean;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  parentId?: string;
  extent?: 'parent' | [[number, number], [number, number]];
  expandParent?: boolean;
};

const edgeTypes: EdgeTypes = {
  custom: CustomEdge,
};

// P0-1: 从 CustomEdge 移至 FlowEditor 层，BFS 只执行 1 次，结果通过 data.isActive 共享
function getConnectedNodes(
  startNodeId: string,
  edges: Array<{ from: string; to: string }>
): Set<string> {
  const connected = new Set<string>();
  const queue = [startNodeId];
  connected.add(startNodeId);

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    for (const edge of edges) {
      if (edge.from === currentId && !connected.has(edge.to)) {
        connected.add(edge.to);
        queue.push(edge.to);
      }
      if (edge.to === currentId && !connected.has(edge.from)) {
        connected.add(edge.from);
        queue.push(edge.from);
      }
    }
  }

  return connected;
}

interface FlowEditorProps {
  onNodeAction: (id: string, prompt?: string) => void;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>) => void;
  onNodeDelete: (id: string) => void;
  onAddNode: (type: NodeType, position?: { x: number; y: number }, initialData?: any) => string | undefined;
  onExtractImage?: (nodeId: string, image: string) => void;
  onGeneratePanorama?: (sourceNodeId: string, params: {
    prompt: string;
    quality: import('../../types').PanoramaQuality;
    referenceImageUrl?: string;
    mode: 'txt2panorama' | 'img2panorama';
  }) => void;
  onToggleChat?: () => void;
  onOpenChat?: () => void;
}

interface NodePickerState {
  visible: boolean;
  x: number;
  y: number;
  sourceNodeId?: string;
}

// #5: 提取为模块级常量，避免每次渲染重建
const NODE_PICKER_OPTIONS = [
  { type: NodeType.PROMPT_INPUT, icon: Type, name: '创意描述', color: 'text-amber-400' },
  { type: NodeType.MEDIA_UPLOAD, icon: Upload, name: '媒体上传', color: 'text-orange-400' },
  { type: NodeType.IMAGE_GENERATOR, icon: ImageIcon, name: '图片生成', color: 'text-cyan-400' },
  { type: NodeType.VIDEO_GENERATOR, icon: Film, name: '视频生成', color: 'text-purple-400' },
  { type: NodeType.AUDIO_GENERATOR, icon: Mic2, name: '灵感音乐', color: 'text-pink-400' },
  { type: NodeType.VIDEO_ANALYZER, icon: Scan, name: '视频分析', color: 'text-emerald-400' },
  { type: NodeType.IMAGE_EDITOR, icon: Edit2, name: '图像编辑', color: 'text-rose-400' },
  { type: NodeType.TEXT_NODE, icon: Type, name: '文本节点', color: 'text-amber-400' },
  { type: NodeType.SCRIPT_NODE, icon: FileText, name: '脚本生成器', color: 'text-emerald-400' },
  { type: NodeType.VIDEO_COMPOSE, icon: Scissors, name: '视频合成', color: 'text-slate-400' },
] as const;

// #1: memo + 自定义比较函数，防止父组件重渲染时牵连菜单
const NodePickerMenu = memo(({
  visible, x, y, onSelect, onClose,
}: {
  visible: boolean;
  x: number;
  y: number;
  onSelect: (type: NodeType) => void;
  onClose: () => void;
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // #4: useMemo 同步计算位置，消除 useEffect 二次渲染
  const adjustedPos = useMemo(() => {
    if (!visible) return { x, y };
    const MENU_WIDTH = 180;
    const MENU_HEIGHT = 420;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const PADDING = 16;
    let ax = x;
    let ay = y;
    if (ax + MENU_WIDTH > vw - PADDING) ax = vw - PADDING - MENU_WIDTH;
    if (ay + MENU_HEIGHT > vh - PADDING) ay = vh - PADDING - MENU_HEIGHT;
    if (ax < PADDING) ax = PADDING;
    if (ay < PADDING) ay = PADDING;
    return { x: ax, y: ay };
  }, [visible, x, y]);

  // click-outside 关闭
  useEffect(() => {
    if (!visible) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div
      ref={menuRef}
      className="fixed z-[1000] bg-[#1c1c1e]/95 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-2xl p-2 min-w-[160px]"
      style={{ left: adjustedPos.x, top: adjustedPos.y }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-3 py-2 border-b border-white/5 mb-1">
        选择节点类型
      </div>
      {NODE_PICKER_OPTIONS.map((option) => (
        <div
          key={option.type}
          onClick={() => { onSelect(option.type); onClose(); }}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-default hover:bg-white/10 transition-colors group"
        >
          <option.icon size={16} className={`${option.color} group-hover:scale-110 transition-transform`} />
          <span className="text-xs font-medium text-slate-300 group-hover:text-white">{option.name}</span>
        </div>
      ))}
    </div>
  );
}, (prev, next) => {
  // 自定义比较：只有 visible/x/y 变化时才重渲染，onSelect/onClose 引用变化不触发
  return prev.visible === next.visible && prev.x === next.x && prev.y === next.y;
});

const FlowEditorInner: React.FC<FlowEditorProps> = ({
  onNodeAction,
  onNodeUpdate,
  onNodeDelete,
  onAddNode,
  onExtractImage,
  onGeneratePanorama,
  onToggleChat,
  onOpenChat,
}) => {
  const nodes = useNodeStore((s) => s.nodes);
  const connections = useNodeStore((s) => s.connections);
  const selectedNodeIdsSet = useSelectionStore((s) => s.selectedNodeIds);
  const selectedNodeIds = useMemo(() => Array.from(selectedNodeIdsSet), [selectedNodeIdsSet]);
  const setSelectedNodeIds = useSelectionStore((s) => s.setSelectedNodeIds);
  const setActiveGroup = useGroupStore((s) => s.setActiveGroup);
  const createGroup = useGroupStore((s) => s.createGroup);
  const checkNodeEnterGroup = useCanvasStore((s) => s.checkNodeEnterGroup);
  const checkNodeExitGroup = useCanvasStore((s) => s.checkNodeExitGroup);
  // P1: defaultViewport 只在 ReactFlow 挂载时生效一次，改为只读一次的 ref
  // 之前用响应式订阅 viewportX/Y/Zoom，每次 canvasStore 更新都触发 FlowEditorInner 重渲染
  // 但 defaultViewport 的变化对已挂载的 ReactFlow 无效，完全多余
  const initialViewportRef = useRef(() => {
    const state = useCanvasStore.getState();
    return { x: state.x, y: state.y, zoom: state.zoom };
  });
  const initialViewport = initialViewportRef.current();
  const setViewport = useCanvasStore((s) => s.setViewport);
  const reactFlowInstance = useReactFlow();
  const [nodePicker, setNodePicker] = useState<NodePickerState>({ visible: false, x: 0, y: 0 });
  const nodePickerRef = useRef(nodePicker);
  nodePickerRef.current = nodePicker;
  const [isSelecting, setIsSelecting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const connectingSourceRef = useRef<string | null>(null);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nodesRef = useRef(nodes);
  const selectedNodeIdsRef = useRef(selectedNodeIds);
  const isDraggingRef = useRef(false);
  const draggedNodeIdsRef = useRef<string[]>([]);
  const pendingPositionUpdatesRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const rafIdRef = useRef<number | null>(null);
  // P1-1: 拖拽停止后跳过一次全量重建，因为 localNodes 已在拖拽过程中更新
  const skipNextNodesRebuildRef = useRef(false);
  // resize 期间跳过全量重建：resize 只改变单个节点的 width/height，
  // 通过 onNodesChange 局部更新 localNodes，避免 buildFlowNodes 为所有节点新建 wrapper
  // 否则 ReactFlow 会对所有节点做 diff/协调，触发 backdrop-filter 重新合成 → 频闪
  const resizingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  // 订阅 groupStore.resizingGroupId：GroupNode resize 开始/结束时同步 resizingRef
  // TextNode resize 通过 callbacksRef.onResizeStart/onResizeEnd 同步（见 useSelfWriteEditor）
  useEffect(() => {
    let prev = useGroupStore.getState().resizingGroupId;
    resizingRef.current = prev !== null;
    return useGroupStore.subscribe((state) => {
      const current = state.resizingGroupId;
      if (current !== prev) {
        prev = current;
        resizingRef.current = current !== null;
      }
    });
  }, []);

  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    selectedNodeIdsRef.current = selectedNodeIds;
  }, [selectedNodeIds]);

  const buildFlowNodes = useCallback((appNodes: AppNode[], existingLocalNodes?: FlowNode[]) => {
    const nodeIds = new Set(appNodes.map(n => n.id));
    const nodeMap = new Map(appNodes.map(n => [n.id, n]));
    const existingMap = existingLocalNodes
      ? new Map(existingLocalNodes.map(ln => [ln.id, ln]))
      : null;
    const currentSelectedIds = useSelectionStore.getState().selectedNodeIds;
    return appNodes.map(n => {
      const existing = existingMap?.get(n.id);
      const hasValidParent = n.parentNode && nodeIds.has(n.parentNode);
      let posX = n.x;
      let posY = n.y;
      if (hasValidParent) {
        const parent = nodeMap.get(n.parentNode!);
        if (parent) {
          posX = n.x - parent.x;
          posY = n.y - parent.y;
        }
      }
      const isGroup = isGroupNode(n);
      return {
        id: n.id,
        type: n.type,
        position: { x: posX, y: posY },
        data: isGroup
          ? { ...n.data, nodeWidth: n.width, nodeHeight: n.height } as unknown as Record<string, unknown>
          : n as unknown as Record<string, unknown>,
        selected: currentSelectedIds.has(n.id),
        ...(isGroup && n.width && n.height ? {
          width: n.width,
          height: n.height,
        } : {}),
        ...(hasValidParent ? { parentId: n.parentNode! } : {}),
        ...(hasValidParent && n.extent ? { extent: n.extent } : {}),
        ...(hasValidParent && n.expandParent ? { expandParent: n.expandParent } : {}),
      };
    });
  }, []);

  const initialNodes = useMemo(() => buildFlowNodes(nodes), []);

  const [localNodes, setLocalNodes, onNodesChangeLocal] = useNodesState<FlowNode>(initialNodes);
  const [localEdges, setLocalEdges, onEdgesChangeLocal] = useEdgesState<Edge>([]);
  const localNodesRef = useRef(localNodes);
  const localEdgesRef = useRef(localEdges);

  useEffect(() => {
    localNodesRef.current = localNodes;
  }, [localNodes]);

  useEffect(() => {
    localEdgesRef.current = localEdges;
  }, [localEdges]);

  useEffect(() => {
    if (isDraggingRef.current) return;
    // P1-1: 拖拽停止后跳过一次全量重建
    // localNodes 的位置已在拖拽过程中通过 onNodesChangeLocal 更新，
    // nodeStore 的位置通过 updateNodePositions 更新，两者一致，无需重建。
    // 跳过重建可避免 ReactFlow diff 所有节点，让 dragging:false 的更新立即执行。
    if (skipNextNodesRebuildRef.current) {
      skipNextNodesRebuildRef.current = false;
      return;
    }
    // resize 期间跳过全量重建：只更新被 resize 节点的 width/height
    // buildFlowNodes 会为所有节点新建 wrapper 对象 → ReactFlow diff 所有节点
    // → backdrop-filter 重新合成 → 频闪
    if (resizingRef.current || isResizingAnyNode.current) {
      // 局部更新：对比 nodes 与 localNodes，只更新尺寸变化的节点
      setLocalNodes(prev => {
        const nodeMap = new Map(nodes.map(n => [n.id, n]));
        let changed = false;
        const next = prev.map(ln => {
          const appNode = nodeMap.get(ln.id);
          if (!appNode) return ln;
          const newWidth = appNode.width;
          const newHeight = appNode.height;
          const newGroupData = isGroupNode(appNode);
          if (newGroupData && (newWidth !== undefined || newHeight !== undefined)) {
            const oldWidth = ln.width;
            const oldHeight = ln.height;
            if (newWidth !== oldWidth || newHeight !== oldHeight) {
              changed = true;
              return {
                ...ln,
                width: newWidth,
                height: newHeight,
                data: { ...appNode.data, nodeWidth: newWidth, nodeHeight: newHeight } as unknown as Record<string, unknown>,
              };
            }
          }
          // 非组节点的 data 引用变化（updateNode 会创建新 data 对象）
          // 只比较 data 引用，引用相同则跳过
          const currentAppNode = ln.data as unknown as AppNode;
          if (currentAppNode !== appNode) {
            changed = true;
            return { ...ln, data: appNode as unknown as Record<string, unknown> };
          }
          return ln;
        });
        return changed ? next : prev;
      });
      return;
    }
    const newFlowNodes = buildFlowNodes(nodes, localNodesRef.current);
    setLocalNodes(newFlowNodes);
  }, [nodes, setLocalNodes]);

  // P1-2: 选中变化时只更新 selected 字段，避免全量重建 localNodes
  // 之前 selectedNodeIdsSet 变化会触发 buildFlowNodes 全量重建，导致 ReactFlow diff 所有节点
  // 现在只更新 selected 字段，ReactFlow 只重渲染 selected 变化的节点
  useEffect(() => {
    if (isDraggingRef.current) return;
    setLocalNodes(prev => {
      let changed = false;
      const next = prev.map(n => {
        const isSelected = selectedNodeIdsSet.has(n.id);
        if (n.selected !== isSelected) {
          changed = true;
          return { ...n, selected: isSelected };
        }
        return n;
      });
      return changed ? next : prev;
    });
  }, [selectedNodeIdsSet, setLocalNodes]);
  
  // P0-1: 在 FlowEditor 层计算 activeEdgeIds，BFS 只执行 1 次
  const activeEdgeIds = useMemo(() => {
    if (selectedNodeIds.length === 0) return new Set<string>();
    const connectedSet = getConnectedNodes(selectedNodeIds[0], connections);
    const active = new Set<string>();
    for (const c of connections) {
      if (connectedSet.has(c.from) && connectedSet.has(c.to)) {
        active.add(`${c.from}-${c.to}`);
      }
    }
    return active;
  }, [selectedNodeIds, connections]);

  // P0-2: 更新 edgeActiveStore，不再通过 data 传入 isActive
  useEffect(() => {
    useEdgeActiveStore.getState().setActiveEdgeIds(activeEdgeIds);
  }, [activeEdgeIds]);

  // 边数据不再包含 isActive，只在 connections 变化时重建
  useEffect(() => {
    const newFlowEdges = connections.map(c => ({
      id: `${c.from}-${c.to}`,
      source: c.from,
      target: c.to,
      type: 'custom',
    }));
    setLocalEdges(newFlowEdges);
  }, [connections, setLocalEdges]);

  const onNodesChange: OnNodesChange<FlowNode> = useCallback((changes) => {
    const hasPositionChange = changes.some(c => c.type === 'position');
    const isDraggingNow = isDraggingRef.current;

    if (hasPositionChange && isDraggingNow) {
      const positionChanges = changes.filter((c): c is NodePositionChange => c.type === 'position' && 'position' in c && !!c.position);
      for (const c of positionChanges) {
        if (c.position) {
          pendingPositionUpdatesRef.current.set(c.id, c.position);
        }
      }
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = null;
          const pending = pendingPositionUpdatesRef.current;
          pendingPositionUpdatesRef.current = new Map();
          const positionChanges = Array.from(pending.entries()).map(([id, position]) => ({
            id,
            type: 'position' as const,
            position,
          }));
          onNodesChangeLocal(positionChanges);
        });
      }
    } else {
      onNodesChangeLocal(changes);
    }
    
    const hasSelectionChange = changes.some(c => c.type === 'select');
    const hasRemove = changes.some(c => c.type === 'remove');
    
    if (hasSelectionChange && !isSelecting) {
      const selectChanges = changes.filter(c => c.type === 'select');
      const newlySelected = selectChanges.filter((c): c is { id: string; type: 'select'; selected: boolean } => c.selected === true);
      
      if (newlySelected.length === 1) {
        setSelectedNodeIds([newlySelected[0].id]);
      } else if (newlySelected.length > 1) {
        setSelectedNodeIds(newlySelected.map(c => c.id));
      } else {
        const deselectChanges = selectChanges.filter((c): c is { id: string; type: 'select'; selected: boolean } => c.selected === false);
        setSelectedNodeIds(selectedNodeIdsRef.current.filter(id => !deselectChanges.some(c => c.id === id)));
      }
    }
    
    if (hasRemove) {
      for (const change of changes) {
        if (change.type === 'remove') {
          onNodeDelete(change.id);
        }
      }
    }
    
    const hasDragStart = changes.some(c => c.type === 'position' && 'dragging' in c && c.dragging === true);
    const hasDragStop = changes.some(c => c.type === 'position' && 'dragging' in c && c.dragging === false);
    
    if (hasDragStart) {
      isDraggingRef.current = true;
      setIsDragging(true);
      window.dispatchEvent(new CustomEvent('flow-drag-start'));
      const dragStartChanges = changes.filter((c): c is NodePositionChange & { dragging: boolean } => c.type === 'position' && 'dragging' in c && c.dragging === true);
      draggedNodeIdsRef.current = dragStartChanges.map((c) => c.id);
    }
    
    if (hasDragStop && isDraggingRef.current) {
      isDraggingRef.current = false;
      setIsDragging(false);
      window.dispatchEvent(new CustomEvent('flow-drag-stop'));
      const positionChanges = changes.filter((c): c is { id: string; type: 'position'; position: { x: number; y: number } } => c.type === 'position' && 'position' in c && !!c.position);
      if (positionChanges.length > 0) {
        const currentNodes = nodesRef.current;
        const nodeMap = new Map(currentNodes.map(n => [n.id, n]));

        // P2-1: 计算所有需要更新的节点位置（包括被拖拽节点和其子节点）
        const positionUpdates: Array<{ id: string; x: number; y: number }> = [];

        // 1. 先处理被拖拽节点本身
        positionChanges.forEach(change => {
          const node = nodeMap.get(change.id);
          if (node && change.position) {
            if (node.parentNode) {
              const parent = nodeMap.get(node.parentNode);
              if (parent) {
                positionUpdates.push({
                  id: change.id,
                  x: change.position.x + parent.x,
                  y: change.position.y + parent.y,
                });
              }
            } else {
              positionUpdates.push({
                id: change.id,
                x: change.position.x,
                y: change.position.y,
              });
            }
          }
        });

        // 2. 处理被拖拽组节点的子节点（子节点跟随组移动）
        const groupDeltas: Map<string, { deltaX: number; deltaY: number }> = new Map();
        positionChanges.forEach(change => {
          const node = nodeMap.get(change.id);
          if (node && isGroupNode(node) && change.position) {
            groupDeltas.set(change.id, {
              deltaX: change.position.x - node.x,
              deltaY: change.position.y - node.y,
            });
          }
        });

        // 为有子节点的组添加子节点位置更新
        if (groupDeltas.size > 0) {
          currentNodes.forEach(n => {
            if (n.parentNode && groupDeltas.has(n.parentNode)) {
              const delta = groupDeltas.get(n.parentNode)!;
              positionUpdates.push({
                id: n.id,
                x: n.x + delta.deltaX,
                y: n.y + delta.deltaY,
              });
            }
          });
        }

        // P2-1: 使用局部更新方法，保持未拖拽节点引用不变
        // 之前 setNodes(finalNodes) 创建全新数组，导致 useInputAssets 的 useShallow 判定不等，
        // 触发所有 GenerateNode 重渲染。现在只更新被拖拽节点，未拖拽节点引用不变。
        useNodeStore.getState().updateNodePositions(positionUpdates);

        // P1-1: 标记跳过下一次 useEffect[nodes] 的全量重建
        // localNodes 的位置已在拖拽过程中通过 onNodesChangeLocal 更新，
        // 此处 updateNodePositions 只更新 nodeStore，不需要重建 localNodes。
        skipNextNodesRebuildRef.current = true;

        // P2-3: 用双 rAF 替代 setTimeout，确保在浏览器空闲时执行
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const currentNodes = useNodeStore.getState().nodes;
            
            draggedNodeIdsRef.current.forEach(nodeId => {
              const node = currentNodes.find(n => n.id === nodeId);
              if (node && !isGroupNode(node)) {
                if (node.groupId) {
                  checkNodeExitGroup(nodeId);
                } else {
                  checkNodeEnterGroup(nodeId);
                }
              }
            });
            
            draggedNodeIdsRef.current = [];
          });
        });
      }
    }
  }, [onNodesChangeLocal, onNodeDelete, setSelectedNodeIds, isSelecting, checkNodeEnterGroup, checkNodeExitGroup]);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    onEdgesChangeLocal(changes);
    for (const change of changes) {
      if (change.type === 'remove') {
        const edge = localEdgesRef.current.find(e => e.id === change.id);
        if (edge) {
          useNodeStore.getState().removeConnection(edge.source, edge.target);
        }
      }
    }
  }, [onEdgesChangeLocal]);

  const onConnect = useCallback((params: Connection) => {
    if (params.source && params.target) {
      useNodeStore.getState().addConnection(params.source, params.target);
    }
  }, []);

  const handleFitView = useCallback(() => {
    reactFlowInstance.fitView({ padding: 0.2 });
  }, [reactFlowInstance]);

  const handleConnectEnd = useCallback((event: MouseEvent | TouchEvent, connectionState: Record<string, unknown>) => {
    console.log('handleConnectEnd triggered', { connectionState, sourceRef: connectingSourceRef.current });
    if (!connectionState.toNode && connectingSourceRef.current) {
      const clientX = 'clientX' in event ? (event as MouseEvent).clientX : 0;
      const clientY = 'clientY' in event ? (event as MouseEvent).clientY : 0;
      console.log('Showing node picker at', clientX, clientY);
      setNodePicker({
        visible: true,
        x: clientX,
        y: clientY,
        sourceNodeId: connectingSourceRef.current,
      });
      connectingSourceRef.current = null;
    }
  }, []);

  const handleConnectStart = useCallback((_event: MouseEvent | TouchEvent, params: { nodeId: string | null; handleId: string | null; handleType: string | null }) => {
    console.log('handleConnectStart triggered', params);
    if (params.handleType === 'source' && params.nodeId) {
      connectingSourceRef.current = params.nodeId;
      console.log('Set connectingSourceRef to', params.nodeId);
    }
  }, []);

  const handlePaneContextMenu = useCallback((event: MouseEvent | React.MouseEvent) => {
    event.preventDefault();
    const clientX = 'clientX' in event ? (event as MouseEvent).clientX : 0;
    const clientY = 'clientY' in event ? (event as MouseEvent).clientY : 0;
    setNodePicker({
      visible: true,
      x: clientX,
      y: clientY,
      sourceNodeId: undefined,
    });
  }, []);

  // 修复A: handlePaneClick 加定时器，双击时跳过，避免双击触发2次无效的 store 更新
  const handlePaneClick = useCallback(() => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      return; // 双击的第二次 click，跳过
    }
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      if (nodePickerRef.current.visible) {
        setNodePicker({ visible: false, x: 0, y: 0 });
      }
      setActiveGroup(null);
      useCanvasStore.getState().deselectEdges();
    }, 200);
  }, [setActiveGroup]);

  // 修复B+C: 使用 ReactFlow onPaneDoubleClick，取消残留 click 定时器
  const handleDoubleClick = useCallback((event: MouseEvent | React.MouseEvent) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    const clientX = 'clientX' in event ? (event as MouseEvent).clientX : 0;
    const clientY = 'clientY' in event ? (event as MouseEvent).clientY : 0;
    setNodePicker({
      visible: true,
      x: clientX,
      y: clientY,
      sourceNodeId: undefined,
    });
  }, []);

  const handleNodeClick = useCallback((_: React.MouseEvent, node: FlowNode) => {
    if (isGroupNode(node as unknown as AppNode)) {
      setActiveGroup(node.id);
    }
  }, [setActiveGroup]);

  const handleNodeDragStart = useCallback((_: React.MouseEvent, node: FlowNode) => {
  }, []);

  // #3: onSelect 用 ref 间接调用，不再依赖 nodePicker 对象，切断自毁链
  const handleNodePickerSelect = useCallback((type: NodeType) => {
    const picker = nodePickerRef.current;
    const position = reactFlowInstance.screenToFlowPosition({
      x: picker.x,
      y: picker.y,
    });
    const newNodeId = onAddNode(type, position ? { x: position.x, y: position.y } : undefined);
    if (newNodeId && picker.sourceNodeId) {
      useNodeStore.getState().addConnection(picker.sourceNodeId!, newNodeId);
    }
    setNodePicker({ visible: false, x: 0, y: 0 });
  }, [onAddNode, reactFlowInstance]);

  // #2: onClose 用 useCallback，不再内联箭头函数
  const closeNodePicker = useCallback(() => setNodePicker({ visible: false, x: 0, y: 0 }), []);

  const handleSelectionStart = useCallback(() => {
    setIsSelecting(true);
    useSelectionStore.getState().setBoxSelecting(true);
  }, []);

  const handleSelectionEnd = useCallback(() => {
    setIsSelecting(false);
    useSelectionStore.getState().setBoxSelecting(false);
    const currentlySelected = localNodes.filter(n => n.selected).map(n => n.id);
    setSelectedNodeIds(currentlySelected);
  }, [localNodes, setSelectedNodeIds]);

  const callbacksRef = useRef({ onNodeAction, onNodeUpdate, onNodeDelete, onExtractImage, onAddNode, onGeneratePanorama, onToggleChat, onOpenChat });
  callbacksRef.current = { onNodeAction, onNodeUpdate, onNodeDelete, onExtractImage, onAddNode, onGeneratePanorama, onToggleChat, onOpenChat };

  const NodeWithZoom = useCallback((props: Record<string, unknown>) => {
    const { selected, id, type, position, data, width, height, dragging, ...rest } = props;
    const nodeName = NODE_TYPE_CONFIG[type as NodeType]?.name || '节点';
    const cb = callbacksRef.current;
    return (
      <NodeErrorBoundary nodeId={String(id)} nodeName={nodeName}>
        <GenerateNode
          data={data as AppNode}
          selected={Boolean(selected)}
          dragging={Boolean(dragging)}
          onNodeAction={cb.onNodeAction}
          onNodeUpdate={cb.onNodeUpdate}
          onNodeDelete={cb.onNodeDelete}
          onExtractImage={cb.onExtractImage}
          onAddNode={cb.onAddNode}
          onGeneratePanorama={cb.onGeneratePanorama}
          onToggleChat={cb.onToggleChat}
          onOpenChat={cb.onOpenChat}
        />
      </NodeErrorBoundary>
    );
  }, []);

  const customNodeTypes = useMemo(() => {
    return {
      [NodeType.PROMPT_INPUT]: NodeWithZoom,
      [NodeType.MEDIA_UPLOAD]: NodeWithZoom,
      [NodeType.IMAGE_GENERATOR]: NodeWithZoom,
      [NodeType.VIDEO_GENERATOR]: NodeWithZoom,
      [NodeType.AUDIO_GENERATOR]: NodeWithZoom,
      [NodeType.VIDEO_ANALYZER]: NodeWithZoom,
      [NodeType.IMAGE_EDITOR]: NodeWithZoom,
      [NodeType.PANORAMA_GENERATOR]: NodeWithZoom,
      [NodeType.GROUP]: GroupNode,
    } as NodeTypes;
  }, [NodeWithZoom]);

  const multiSelectBounds = useMemo(() => {
    if (selectedNodeIds.length < 2 || isDragging || isSelecting) return null;

    try {
      const rect = reactFlowInstance.getNodesBounds(selectedNodeIds);
      if (!rect || rect.width === 0 || rect.height === 0) return null;

      return {
        canvasX: rect.x,
        canvasY: rect.y,
        canvasWidth: rect.width,
        canvasHeight: rect.height,
      };
    } catch {
      return null;
    }
  }, [selectedNodeIds, reactFlowInstance, isDragging, isSelecting]);

  const debouncedSetViewport = useMemo(
    () => debounce((vx: number, vy: number, vz: number) => {
      setViewport(vx, vy, vz);
    }, 60),
    [setViewport]
  );

  return (
    <div className={`w-full h-full ${isDragging ? 'flow-dragging' : ''}`} ref={containerRef} onDoubleClick={handleDoubleClick}>
      <EdgeParticlesCanvas />
      <ReactFlow
        colorMode="dark"
        nodes={localNodes}
        edges={localEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={customNodeTypes}
        edgeTypes={edgeTypes}
        fitView
        snapToGrid
        snapGrid={[15, 15]}
        connectionRadius={30}
        defaultViewport={initialViewport}
        proOptions={{ hideAttribution: true }}
        onMove={(_event, viewport) => {
          debouncedSetViewport(viewport.x, viewport.y, viewport.zoom);
        }}
        selectionOnDrag
        panOnDrag={[1]}
        panOnScroll
        zoomOnDoubleClick={false}
        selectionMode={SelectionMode.Partial}
        minZoom={0.1}
        maxZoom={3}
        onConnectEnd={handleConnectEnd}
        onConnectStart={handleConnectStart}
        onPaneContextMenu={handlePaneContextMenu}
        onPaneClick={handlePaneClick}
        onSelectionStart={handleSelectionStart}
        onSelectionEnd={handleSelectionEnd}
        onlyRenderVisibleElements={false}
        elevateEdgesOnSelect={false}
      >
        <Background color="rgba(255,255,255,0.6)" gap={100} size={3} />
        <Panel position="bottom-left" className="!left-4 !bottom-4">
          <MiniMapToolbar />
        </Panel>
        <ViewportPortal>
          {multiSelectBounds && (
            <InverseScaleBox
              className="absolute -top-[120px] left-1/2 flex justify-center z-30 nodrag"
              style={{
                left: multiSelectBounds.canvasX + multiSelectBounds.canvasWidth / 2,
                top: multiSelectBounds.canvasY - 120,
                transform: 'translateX(-50%)',
                transformOrigin: 'center bottom',
                transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
              }}
              scaleOrigin="center bottom"
            >
              <SelectionToolbar
                selectedNodeIds={selectedNodeIds}
                selectionBox={{
                  x: multiSelectBounds.canvasX - 30,
                  y: multiSelectBounds.canvasY - 50,
                  width: multiSelectBounds.canvasWidth + 60,
                  height: multiSelectBounds.canvasHeight + 80,
                }}
              />
            </InverseScaleBox>
          )}
        </ViewportPortal>
      </ReactFlow>
      <NodePickerMenu
        visible={nodePicker.visible}
        x={nodePicker.x}
        y={nodePicker.y}
        onSelect={handleNodePickerSelect}
        onClose={closeNodePicker}
      />
    </div>
  );
};

export const FlowEditor: React.FC<FlowEditorProps> = memo((props) => {
  return (
    <ReactFlowProvider>
      <FlowEditorInner {...props} />
    </ReactFlowProvider>
  );
});